/** Customer 360° — Overview tab: company, contacts, addresses, tax, terms, account manager, status, ITAM link. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Link2 } from "lucide-react";
import { Section, KV } from "./shared";
import { EntityAddressStrip } from "@/components/address/AddressVerify";

const STATUSES = ["ACTIVE", "PROSPECT", "ON_HOLD", "CREDIT_HOLD", "INACTIVE"];
const addr = (a) => [a.street, a.city, a.state, a.code, a.country].filter(Boolean).join(", ");

export function OverviewTab({ data, id, reload }) {
  const [edit, setEdit] = useState(false);
  const c = data.customer;
  return (
    <div className="grid lg:grid-cols-3 gap-3" data-testid="c360-overview">
      <Section title="Company details" testId="c360-company" right={data.can_edit && (
        <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="c360-edit-profile"><Pencil className="h-3.5 w-3.5 mr-1" />Edit terms & status</Button>)}>
        <KV k="Legal name" v={c.account_name} />
        <KV k="Customer code" v={c.customer_code} mono />
        <KV k="Industry" v={c.industry} />
        <KV k="Type" v={c.account_type} />
        <KV k="Phone" v={c.phone || c.other_phone} mono />
        <KV k="Email" v={c.email || c.email2} />
        <KV k="Website" v={c.website} />
        <KV k="Employees" v={c.employees} />
        <KV k="Customer status" v={<span className="font-mono text-[11px] rounded px-1.5 py-0.5 bg-slate-100" data-testid="c360-status">{data.status}</span>} />
        <KV k="Account manager" v={data.account_manager ? `${data.account_manager.name || ""} ${data.account_manager.email ? `<${data.account_manager.email}>` : ""}`.trim() : null} />
        <KV k="Source" v={c.source} />
        <KV k="Since" v={(c.created_at || "").slice(0, 10)} mono />
      </Section>

      <div className="space-y-3">
        <Section title="Tax & compliance" testId="c360-tax">
          <KV k="GSTIN" v={data.tax.gstin} mono />
          <KV k="PAN" v={data.tax.pan ? <>{data.tax.pan}{data.tax.pan_derived && <span className="ml-2 text-[10px] text-slate-400">derived from GSTIN</span>}</> : null} mono />
        </Section>
        <Section title="Credit & payment terms" testId="c360-terms">
          <KV k="Credit terms" v={data.terms.credit_terms} />
          <KV k="Payment terms" v={data.terms.payment_terms} />
          <KV k="Credit limit" v={data.terms.credit_limit_inr != null ? `₹${Number(data.terms.credit_limit_inr).toLocaleString("en-IN")}` : null} mono />
        </Section>
        <Section title="ITAM company link" testId="c360-itam-link" right={data.can_edit && <LinkButton id={id} current={data.linked_master_company_id} reload={reload} />}>
          {data.itam_company
            ? <><KV k="Company" v={data.itam_company.name} /><KV k="Code" v={data.itam_company.code} mono /><KV k="Link" v={data.link_source === "profile" ? "Set by staff" : "Matched by name"} /></>
            : <p className="text-xs text-slate-500">Not linked to an ITAM master company — devices, deployments and portal data will appear once linked.</p>}
        </Section>
      </div>

      <div className="space-y-3">
        <Section title="Billing address" testId="c360-billing"><p className="text-[13px] text-slate-800">{addr(data.billing_address) || <span className="text-slate-300">—</span>}</p></Section>
        <Section title="Address verification" testId="c360-address-verify"><EntityAddressStrip entityType="CUSTOMER" entityId={id} customerAccountId={id} onChanged={reload} single /></Section>
        <Section title="Shipping addresses" count={data.shipping_addresses.length} testId="c360-shipping">
          {data.shipping_addresses.length === 0 && <p className="text-xs text-slate-400">No shipping addresses on file.</p>}
          {data.shipping_addresses.map((a, i) => (
            <div key={i} className="py-1.5 border-b border-slate-50 last:border-0">
              <div className="text-[11px] text-slate-500">{a.label}</div>
              <div className="text-[13px] text-slate-800">{addr(a)}</div>
            </div>
          ))}
        </Section>
        <Section title="Contacts" count={data.contacts.length} testId="c360-contacts">
          {data.contacts.length === 0 && <p className="text-xs text-slate-400">No contacts yet.</p>}
          {data.contacts.map((ct) => (
            <div key={ct.id} className="py-1.5 border-b border-slate-50 last:border-0 text-[13px]">
              <div className="flex items-center gap-2"><Link to={`/one/relationships/people/${ct.id}`} className="font-medium text-slate-900 hover:text-sky-700 hover:underline" data-testid={`c360-contact-p360-${ct.id}`}>{ct.name}</Link>{ct.is_primary && <span className="text-[10px] rounded bg-sky-50 text-sky-700 px-1.5">primary</span>}<span className="text-slate-400 text-[11px]">{ct.designation}</span></div>
              <div className="text-[12px] text-slate-500 font-mono">{[ct.email, ct.mobile || ct.phone].filter(Boolean).join(" · ")}</div>
            </div>
          ))}
        </Section>
      </div>

      <Dialog open={edit} onOpenChange={setEdit}>
        <DialogContent className="max-w-md" data-testid="c360-profile-dialog">
          <DialogHeader><DialogTitle className="text-base">Terms, status & ownership</DialogTitle></DialogHeader>
          <ProfileForm id={id} data={data} onDone={() => { setEdit(false); reload(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProfileForm({ id, data, onDone }) {
  const [f, setF] = useState({ credit_terms: data.terms.credit_terms || "", payment_terms: data.terms.payment_terms || "", credit_limit_inr: data.terms.credit_limit_inr ?? "",
    account_manager_email: data.account_manager?.email || "", status: data.status, pan: data.tax.pan_derived ? "" : (data.tax.pan || "") });
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/one-today/assignees").then(({ data }) => setUsers(data.items || [])).catch(() => {}); }, []);
  const save = async () => {
    try {
      await api.put(`/customer-360/${id}/profile`, { ...f, credit_limit_inr: f.credit_limit_inr === "" ? null : Number(f.credit_limit_inr), pan: f.pan || null, account_manager_email: f.account_manager_email || null });
      toast.success("Customer profile updated"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div className="space-y-2.5 text-sm">
      <label className="block"><span className="text-xs text-slate-500">Credit terms</span><Input value={f.credit_terms} onChange={set("credit_terms")} placeholder="e.g. Net 30" data-testid="c360-f-credit" /></label>
      <label className="block"><span className="text-xs text-slate-500">Payment terms</span><Input value={f.payment_terms} onChange={set("payment_terms")} placeholder="e.g. 50% advance, balance on delivery" data-testid="c360-f-payment" /></label>
      <label className="block"><span className="text-xs text-slate-500">Credit limit (₹)</span><Input type="number" value={f.credit_limit_inr} onChange={set("credit_limit_inr")} data-testid="c360-f-limit" /></label>
      <label className="block"><span className="text-xs text-slate-500">PAN</span><Input value={f.pan} onChange={set("pan")} placeholder="ABCDE1234F" className="font-mono uppercase" data-testid="c360-f-pan" /></label>
      <label className="block"><span className="text-xs text-slate-500">Account manager</span>
        <select value={f.account_manager_email} onChange={set("account_manager_email")} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="c360-f-manager">
          <option value="">— none —</option>{users.map((u) => <option key={u.email} value={u.email}>{u.name || u.email} · {u.role}</option>)}
        </select></label>
      <label className="block"><span className="text-xs text-slate-500">Customer status</span>
        <select value={f.status} onChange={set("status")} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="c360-f-status">
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select></label>
      <Button onClick={save} data-testid="c360-profile-save" className="w-full">Save</Button>
    </div>
  );
}

function LinkButton({ id, current, reload }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => { if (open) api.get("/customer-360/lookup/master-companies", { params: { q } }).then(({ data }) => setItems(data.items || [])).catch(() => {}); }, [open, q]);
  const link = async (mcId) => {
    try { await api.put(`/customer-360/${id}/profile`, { linked_master_company_id: mcId }); toast.success("ITAM company linked"); setOpen(false); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="c360-link-itam"><Link2 className="h-3.5 w-3.5 mr-1" />{current ? "Change link" : "Link company"}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="c360-link-dialog">
          <DialogHeader><DialogTitle className="text-base">Link ITAM master company</DialogTitle></DialogHeader>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search master companies…" data-testid="c360-link-search" />
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {items.map((m) => (
              <button key={m.id} onClick={() => link(m.id)} data-testid={`c360-link-pick-${m.id}`} className={`w-full text-left px-2 py-2 text-sm hover:bg-slate-50 ${m.id === current ? "bg-sky-50" : ""}`}>
                <span className="text-slate-900">{m.name}</span> <span className="text-[11px] text-slate-400 font-mono ml-2">{m.code} · {m.country}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
